/**
 * "This login has no employee record" — remembered for a short while per tab.
 *
 * An admin with no employee record gets a 404 from `/employees/me` on every
 * page load. The bootstrap remembers that answer so later loads skip the
 * request. It is only a hint, so it must not outlive the fact:
 *   - keyed by the user (the JWT subject), never applied to someone else;
 *   - it expires after `TTL_MS`, so a record HR links later is found;
 *   - a fresh sign-in, a sign-out, or any 200 from `/employees/me` clears it.
 *
 * sessionStorage (this tab only); a blocked storage just means asking again.
 */
const KEY = "linkhr:no-employee";
export const NO_EMPLOYEE_TTL_MS = 10 * 60_000;

interface Stored {
  userId: string;
  at: number;
}

function read(): Stored | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw === null) return null;
    const v = JSON.parse(raw) as Partial<Stored>;
    return typeof v.userId === "string" && typeof v.at === "number"
      ? { userId: v.userId, at: v.at }
      : null;
  } catch {
    return null;
  }
}

export const noEmployeeMarker = {
  /** Whether `userId` was found without a record less than `TTL_MS` ago. */
  holds(userId: string, now = Date.now()): boolean {
    if (userId === "") return false;
    const v = read();
    return v !== null && v.userId === userId && now - v.at >= 0 && now - v.at < NO_EMPLOYEE_TTL_MS;
  },
  set(userId: string, now = Date.now()): void {
    if (userId === "") return;
    try {
      sessionStorage.setItem(KEY, JSON.stringify({ userId, at: now }));
    } catch {
      // Storage blocked: the next load asks again.
    }
  },
  clear(): void {
    try {
      sessionStorage.removeItem(KEY);
    } catch {
      // Nothing stored.
    }
  },
};
